import { createContext, useContext, type ReactNode } from "react";
import { useSession as useAuthSession } from "./auth-client.ts";

type SessionResult = ReturnType<typeof useAuthSession>;

const SessionContext = createContext<SessionResult | null>(null);

/**
 * Subscribes to the auth session ONCE, at the app root (above the router), and
 * shares it via context.
 *
 * better-auth 1.6's session query refetches whenever its atom's subscriber count
 * goes 0→1 (its `onMount`). Calling `useSession()` directly in several components
 * — combined with route redirects that unmount/remount them — made the atom
 * re-mount repeatedly and fire `/get-session` 4–6× per page load. A single stable
 * subscriber here keeps the count ≥1 for the app's lifetime, so the session is
 * fetched once; every consumer reads it from context via {@link useSession}.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useAuthSession();
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

/** The shared auth session (from {@link SessionProvider}) — no new subscription. */
export function useSession(): SessionResult {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error("useSession must be used within <SessionProvider>");
  }
  return ctx;
}
